import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import './style.scss';
import _ from 'lodash';
import ProgressMessage from 'components/Utils/ProgressMessage';
import List from '@material-ui/core/List';
import SelectableListItem from 'components/Utils/SelectableListItem';
import GenericBadge from 'components/Utils/GenericBadge';
import ErrorBoundary from 'components/Utils/ErrorBoundary';
import DefaultTooltip from 'components/Utils/DefaultTooltip';
import { DefaultSwitch, DefaultSwitch as Switch } from 'services/UIUtils';
import DefaultButton from 'components/Utils/NewDefaultButton';
import DefaultModal from 'components/Utils/DefaultModal';
import DefaultTextField from 'components/Utils/DefaultTextField';
import DefaultDatePicker from 'components/Utils/DefaultDatePicker';
import PerformanceUtils from 'services/PerformanceUtils';
import DefaultSelectField from 'components/Utils/DefaultSelectField';
import MenuItem from '@material-ui/core/MenuItem';
import ReactTable from 'react-table';
import CalendarUtils from 'services/CalendarUtils';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { useAppSelector } from 'careand-redux/store';
import { api } from 'careand-redux/api/ApiThunks';
import MetricChart from 'components/HealthRecords/Graph/MetricChart';
import { Line } from 'react-chartjs-2';
import { Chart, LinearScale } from 'chart.js';
import Annotation from 'chartjs-plugin-annotation';

Chart.register(LinearScale, Annotation);
import Icon from 'components/Utils/NewIcon';
import IconButton from '@material-ui/core/IconButton';
import { IsPatient, IsPhysician } from 'careand-redux/selectors';
import CPPUtils from 'services/CPPUtils';
import { useColors } from '../../../services/colors';

export const INTERVALS = {
  day: 'day',
  month: 'month',
  year: 'year',
};

const DEFAULT_ARRAY = [];

const Graph = ({ patient, defaultMetric = 'summary' }) => {
  const dispatch = useAppDispatch();
  const patientHealthRecords = useAppSelector((state) => state.healthRecords.byId[patient.id]);
  const summaries = patientHealthRecords?.dataSummary ?? DEFAULT_ARRAY;

  const isPatient = useAppSelector(IsPatient);

  const [metric, setMetric] = useState(defaultMetric);
  const [loading, setLoading] = useState(false);
  const [loadingMetrics, setLoadingMetrics] = useState(false);
  const [metrics, setMetrics] = useState({});
  const [summary, setSummary] = useState(convertSummary(summaries));
  const [addModal, setAddModal] = useState(false);
  const [forceRefresh, setForceRefresh] = useState(1);
  const [rawMetrics, setRawMetrics] = useState({});

  const currentMetric = useMemo(() => {
    return metric || Object.keys(summary)[0];
  }, [summary, metric]);

  const diagnostics = useMemo(() => {
    let documents = patientHealthRecords.documents ?? DEFAULT_ARRAY;
    return documents.filter(({ results_text }) => {
      return !!results_text && results_text.length > 0;
    });
  }, [patientHealthRecords]);

  function convertSummary(summaries = []) {
    let result = {};
    summaries.forEach((summary) => {
      let { metric, type, origin } = summary;
      if (metric) {
        let id = metric.id;
        if (type) id += '/' + type;
        id += `_${origin}`;
        result[id] = summary;
      }
    });
    return result;
  }

  const isInitialMount = useRef(true);

  useEffect(() => {
    // On initial mount, use existing Redux data if available instead of re-fetching
    if (isInitialMount.current) {
      isInitialMount.current = false;
      if (summaries.length > 0) {
        setSummary(convertSummary(summaries));
        return;
      }
    }
    if (Object.values(summary).length === 0) setLoading(true);
    dispatch(api.HealthRecords.getHealthDataSummary({ patientId: patient.id })).then((result) => {
      if (result !== undefined) {
        setSummary(convertSummary(result));
      }
      setLoading(false);
    });
  }, [patient.id, dispatch, forceRefresh]);

  useEffect(() => {
    if (!summary || !currentMetric || !summary[currentMetric]) return;
    if (metrics[currentMetric]) return;

    setLoadingMetrics(true);
    let values = summary[currentMetric];

    dispatch(
      api.HealthRecords.getSpecificHealthData({
        patientId: patient.id,
        metric: values.metric.id,
        type: values.type,
        origin: values.origin,
      }),
    ).then((result) => {
      if (result !== undefined) {
        let values = convertMetricData(result, summary[currentMetric]);
        setMetrics({
          ...metrics,
          [currentMetric]: values,
        });
        setRawMetrics((prevState) => ({
          ...prevState,
          [currentMetric]: result,
        }));
      }
      setLoadingMetrics(false);
    });
  }, [dispatch, metrics, currentMetric, summary, patient.id]);

  const metricSummary = summary[currentMetric];
  const chosenMetric = metrics[currentMetric];

  const summaryMetrics = useMemo(() => {
    let array = Object.keys(summary)
      .filter((key) => summary[key].origin !== 'Lab')
      .map((key) => {
        return {
          key,
          data: summary[key],
        };
      });

    array = _.sortBy(array, 'data.metric.name');

    let priority = ['Lab', 'Practitioner'];

    let result = [];

    priority.forEach((origin) => {
      result = [...result, ...array.filter((d) => d.data.origin === origin)];
      array = array.filter((d) => d.data.origin !== origin);
    });

    result = [...result, ...array].map((d) => d.key);

    return result;
  }, [summary]);

  const showAverages = false;

  return (
    <div className="graph full-height">
      <ErrorBoundary>
        <div className="metrics-selection scroll-list full-height underside-right-panel">
          <span className="small-title padded-title">Metrics</span>
          <ProgressMessage size={20} show={loading} topRight={true} />
          <List>
            <SelectableListItem
              className="list-item"
              selected={currentMetric === 'summary'}
              item={
                <GenericBadge title="Blood Work History" info={`${diagnostics.length} Results`} />
              }
              onClick={() => setMetric('summary')}
            />
            {summaryMetrics.map((key) => (
              <SelectableListItem
                key={key}
                className="list-item"
                selected={key === currentMetric}
                item={<MetricBadge summary={summary[key]} onClick={() => setMetric(key)} />}
                onClick={() => setMetric(key)}
              />
            ))}
            {!isPatient && (
              <div className="button-wrapper">
                <DefaultButton
                  name="Add Data"
                  className="blue"
                  onClick={() => setAddModal(true)}
                  icon="plus"
                />
              </div>
            )}
          </List>
        </div>
        {addModal && (
          <DefaultModal
            className="fit-modal add-metrics-modal"
            key="add-metrics-modal"
            title="Add Metrics Data"
            wrap={true}
            padding={true}
            content={
              <AddData
                onSave={() => {
                  setForceRefresh((prevState) => prevState + 1);
                  setAddModal(false);
                }}
                patient={patient}
              />
            }
            isOpen={addModal}
            onRequestClose={() => setAddModal(false)}
          />
        )}
        <div className="graph-wrapper scroll-list full-height full-width" key={currentMetric}>
          {metric === 'summary' && <BloodWorkHistory patient={patient} />}
          {!loading && (
            <>
              <ProgressMessage size={50} show={loadingMetrics} topLeft={true} />
              {!!metricSummary && !!chosenMetric && (
                <>
                  {!isPatient && (
                    <PatientHideMetric patient={patient} metric={metricSummary?.metric?.id} />
                  )}
                  <CareAndChart data={chosenMetric} showAverages={showAverages} />
                  <MetricsTable data={rawMetrics[currentMetric]} />
                </>
              )}
            </>
          )}
        </div>
      </ErrorBoundary>
    </div>
  );
};

const BloodWorkHistory = ({ patient }) => {
  const dispatch = useAppDispatch();
  const [documentsResults, setDocumentsResults] = useState(undefined);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const patientId = patient.id;

  useEffect(() => {
    if (patientId) {
      dispatch(
        api.Documents.getAllDocumentHistoricalResults({
          patientId,
          convertToNumber: false,
        }),
      ).then((results) => {
        setDocumentsResults(_.sortBy(results, ['date_service', 'date']));
        setLoading(false);
      });
    }
  }, [dispatch, patientId]);

  const { lines, columns } = useMemo(() => {
    const lines = {};
    const columns = {};
    let searchLower = search.toLowerCase();
    documentsResults?.forEach((documentResult) => {
      const { results, date, date_service } = documentResult;
      results?.forEach((line) => {
        const { test, result, reference_units, flag } = line;
        if (result.toLowerCase() !== 'pending') {
          if (!search || test.toLowerCase().includes(searchLower)) {
            if (!lines[test]) lines[test] = test;
            let dateString = CalendarUtils.format(line.date || date_service || date, 'DD/MM/YY');
            columns[dateString] = columns[dateString] || {};
            columns[dateString][test] = { result, reference_units, flag };
          }
        }
      });
    });

    // Sort the columns by date in descending order
    const sortedColumns = Object.keys(columns)
      .sort((a, b) => moment(b, 'DD/MM/YY').valueOf() - moment(a, 'DD/MM/YY').valueOf())
      .reverse()
      .reduce((acc, key) => {
        acc[key] = columns[key];
        return acc;
      }, {});

    return { lines, columns: sortedColumns };
  }, [documentsResults, search]);

  if (loading) return <ProgressMessage size={50} topLeft={true} />;

  return (
    <div className="bloodwork-wrapper">
      <div className="mx-3 mb-1">
        <DefaultTextField
          name="filter"
          placeholder="Filter test results"
          onChange={(event) => setSearch(event.target.value)}
          autoFocus={true}
        />
      </div>
      {Object.keys(lines).length === 0 ? (
        <span className="small-title padded-title block">No results to show</span>
      ) : (
        <TableComponent lines={lines} columns={columns} />
      )}
    </div>
  );
};

const TableComponent = ({ lines, columns }) => {
  const [expandedLine, setExpandedLine] = useState(null);
  const colors = useColors();
  const ORDER = [
    'GLUCOSE FASTING',
    'GLUCOSE RANDOM',
    'GLUCOSE',
    'EGFR',
    'CREATININE',
    'CK',
    'URIC ACID',
    'SODIUM',
    'POTASSIUM',
    'ALBUMIN',
    'CBC',
    //Hematology
    'HEMOGLOBIN ELECTROPHORESIS',
    'HEMOGLOBIN',
    'HEMATOCRIT',
    'RBC',
    'MCV',
    'MCH',
    'MCHC',
    'RDW',
    'WBC',
    'PLATELETS',
    'MPV',
    'NEUTROPHILS',
    'LYMPHOCYTES',
    'MONOCYTES',
    'EOSINOPHILS',
    'BASOPHILS',
    //Urinalysis
    'GLUCOSE',
    'BILIRUBIN',
    'BILIRUBIN TOTAL',
    'KETONES',
    'SPECIFIC GRAVITY',
    'BLOOD',
    'pH',
    'PROTEIN',
    'UROBILINOGEN',
    'NITRITE',
    'LEUKOCYTES',
    'COLOUR',
    'APPEARANCE',
    'TSH',
    'T4 FREE',
    'T4 TOTAL',
    'T3 FREE',
    'T3 TOTAL',
    'TSH RECEPTOR AB',
    'ANTI TPO AB',
    'ANTI TG AB',
    'PTH',
    'CHOLESTEROL',
    'TRIGLYCERIDES',
    'HDL CHOLESTEROL',
    'LDL CHOLESTEROL',
    'NON-HDL-CHOLESTEROL',
    'TC/HDL-C RATIO',
    'CHOLESTEROL/HDL CHOLESTEROL',
    'ALT',
    'AST',
    'ASPARTATE AMINOTRANSFERASE (AST)',
    'AST                      INSU',
    'ALKALINE PHOSPHATASE',
    'GGT',
    'FERRITIN',
    'INR',
    'APTT',
    'AMYLASE',
    'LIPASE',
    'CALCIUM',
    'MAGNESIUM',
    'PHOSPHATE',
    'VITAMIN B12',
    'VITAMIN D',
    '25 HYDROXY VITAMIN D',
    'FOLATE',
    'CRP',
    'ESR',
    'RF',
    'ANA',
    'PSA',
    'BETA HCG',
    'ESTROGEN',
    'PROGESTERONE',
    'TESTOSTERONE',
    'DHEA',
    'LH',
    'FSH',
    'CORTISOL',
    'HEPATITIS A,B,C',
    'HBSAG',
    'HIV',
    'VDRL',
    'URINALYSIS',
    'ACR',
    'CULTURE',
  ];

  const REMOVE = [
    'GONORRHOEA',
    'CHLAMYDIA',
    'TRICHOMONAS',
    'UREAPLASMA/MYCOPLASMA',
    'URINE CULTURE',
    'ELECTROCARDIOGRAM',
    'LIPID TARGET VALUES',
  ];

  const headerKeys = _.reverse(Object.keys(columns));
  const lineKeys = Object.keys(lines)
    .filter((key) => !REMOVE.includes(key))
    .sort((a, b) => {
      const aIndex = ORDER.indexOf(a);
      const bIndex = ORDER.indexOf(b);
      if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;
      return a.localeCompare(b);
    });

  return (
    <table>
      <thead>
        <tr>
          <th>Test</th>
          {headerKeys.map((date, index) => {
            let tooltip = moment(date, 'DD/MM/YY').fromNow();
            return (
              <DefaultTooltip title={tooltip} placement="bottom">
                <th key={index}>{date}</th>
              </DefaultTooltip>
            );
          })}
        </tr>
      </thead>
      <tbody>
        {lineKeys.map((line, i) => {
          const isChartable = headerKeys.some((date) => {
            const cell = columns[date][line];
            return cell && !isNaN(parseFloat(cell.result));
          });
          const isExpanded = expandedLine === line;

          let chartContent = null;
          if (isExpanded && isChartable) {
            const points = headerKeys
              .map((date) => {
                const cell = columns[date][line];
                if (!cell || isNaN(parseFloat(cell.result))) return null;
                return {
                  x: moment(date, 'DD/MM/YY').format('YYYY-MM-DD'),
                  y: parseFloat(cell.result),
                  flag: cell.flag || '',
                  referenceUnits: cell.reference_units || '',
                };
              })
              .filter(Boolean);

            if (points.length > 0) {
              // Parse reference range from strict "number-number" format only
              let lower = null;
              let upper = null;
              const refString = points.find((p) => p.referenceUnits)?.referenceUnits || '';
              const rangeMatch = refString.match(/^(\d+\.?\d*)\s*-\s*(\d+\.?\d*)/);
              const lessThanMatch = refString.match(/^<\s*(\d+\.?\d*)/);
              const greaterThanMatch = refString.match(/^>\s*(\d+\.?\d*)/);
              if (rangeMatch) {
                lower = parseFloat(rangeMatch[1]);
                upper = parseFloat(rangeMatch[2]);
              } else if (lessThanMatch) {
                upper = parseFloat(lessThanMatch[1]);
              } else if (greaterThanMatch) {
                lower = parseFloat(greaterThanMatch[1]);
              } else if (refString) {
                console.log(`[Bloodwork] Unparsed reference_units for "${line}":`, refString);
              }

              const FLAG_COLORS = {
                H: '#d16f3e',
                HH: '#be480d',
                A: '#9c27b0',
                L: '#66BDD0',
                LL: '#9ee1ef',
              };
              const NORMAL_COLOR = '#156064';

              const pointColors = points.map((p) => FLAG_COLORS[p.flag] || NORMAL_COLOR);

              const annotations = {};
              if (upper !== null) {
                annotations.upperLimit = {
                  type: 'line',
                  yMin: upper,
                  yMax: upper,
                  borderColor: colors['color-border-default'],
                  borderWidth: 1,
                  borderDash: [6, 4],
                };
              }
              if (lower !== null) {
                annotations.lowerLimit = {
                  type: 'line',
                  yMin: lower,
                  yMax: lower,
                  borderColor: colors['color-border-default'],
                  borderWidth: 1,
                  borderDash: [6, 4],
                };
              }

              const chartOptions = {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: { display: false },
                  tooltip: {
                    mode: 'nearest',
                    axis: 'x',
                    intersect: false,
                    callbacks: { title: (items) => items[0]?.raw?.x },
                  },
                  annotation: Object.keys(annotations).length > 0 ? { annotations } : undefined,
                },
                scales: {
                  x: {
                    type: 'time',
                    time: { unit: 'month' },
                    grid: { display: false },
                  },
                  y: {
                    beginAtZero: false,
                    border: { dash: [10, 5] },
                    grid: { display: true },
                  },
                },
              };

              const flags = points.map((p) => p.flag);
              const HIGH_FLAGS = ['H', 'HH'];
              const LOW_FLAGS = ['L', 'LL'];

              const chartDataConfig = {
                datasets: [
                  {
                    label: line,
                    data: points.map((p) => ({ x: p.x, y: p.y })),
                    fill: false,
                    borderColor: NORMAL_COLOR,
                    backgroundColor: pointColors,
                    pointBackgroundColor: pointColors,
                    pointBorderColor: pointColors,
                    pointRadius: 5,
                    pointHoverRadius: 7,
                    segment: {
                      borderColor: (ctx) => {
                        const f0 = flags[ctx.p0DataIndex];
                        const f1 = flags[ctx.p1DataIndex];
                        if (HIGH_FLAGS.includes(f0) && HIGH_FLAGS.includes(f1))
                          return FLAG_COLORS.H;
                        if (LOW_FLAGS.includes(f0) && LOW_FLAGS.includes(f1)) return FLAG_COLORS.L;
                        if (f0 === 'A' && f1 === 'A') return FLAG_COLORS.A;
                        return NORMAL_COLOR;
                      },
                    },
                  },
                ],
              };

              const hasRange = lower !== null || upper !== null;
              const matchedPart = rangeMatch
                ? rangeMatch[0]
                : lessThanMatch
                ? lessThanMatch[0]
                : greaterThanMatch
                ? greaterThanMatch[0]
                : '';
              const unitSuffix = refString.replace(matchedPart, '').replace(/[()]/g, '').trim();

              chartContent = (
                <tr className="chart-row">
                  <td colSpan={headerKeys.length + 1}>
                    <div className="expanded-chart">
                      {hasRange && (
                        <div className="reference-range">
                          {lower !== null && upper !== null && (
                            <>
                              Reference range: <span className="range-lower">{lower}</span> –{' '}
                              <span className="range-upper">{upper}</span>
                            </>
                          )}
                          {lower === null && upper !== null && (
                            <>
                              Reference: {'<'} <span className="range-upper">{upper}</span>
                            </>
                          )}
                          {lower !== null && upper === null && (
                            <>
                              Reference: {'>'} <span className="range-lower">{lower}</span>
                            </>
                          )}
                          {unitSuffix && <span className="range-unit"> {unitSuffix}</span>}
                        </div>
                      )}
                      <Line data={chartDataConfig} options={chartOptions} />
                    </div>
                  </td>
                </tr>
              );
            }
          }

          return (
            <React.Fragment key={i}>
              <tr
                style={isChartable ? { cursor: 'pointer' } : undefined}
                onClick={
                  isChartable
                    ? () => setExpandedLine((prev) => (prev === line ? null : line))
                    : undefined
                }
              >
                <td>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    {line}
                    {isChartable && <Icon src="chart-line" width="12px" style={{ opacity: 0.4 }} />}
                  </span>
                </td>
                {headerKeys.map((date, j) => {
                  const cell = columns[date][line];
                  if (!cell) return <td key={j} />;
                  let id = `${i}-${j}`;

                  let tooltip = `${cell.reference_units} ${
                    cell.flag ? `(${cell.flag})` : ''
                  }`.trim();
                  return (
                    <DefaultTooltip title={tooltip} placement="right">
                      <td id={id} key={j} className={cell.flag || ''}>
                        {`${cell.result || ''}`}
                      </td>
                    </DefaultTooltip>
                  );
                })}
              </tr>
              {chartContent}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  );
};

export const CareAndChart = ({ data, timeUnit }) => {
  const datasets = useMemo(() => {
    const datasets = {};

    data.forEach((d) => {
      let x = d[0];
      let label = d[1];
      let y = d[2];

      datasets[label] = datasets[label] || { data: [], label };
      datasets[label].data.push({ x, y });
    });

    return Object.values(datasets).map((d) => {
      return {
        ...d,
        data: d.data.sort((a, b) => moment(b.x).unix() - moment(a.x).unix()),
      };
    });
  }, [data]);

  return (
    <div className="plot-wrapper">
      <MetricChart datasets={datasets} timeUnit={timeUnit} />
    </div>
  );
};

const MetricsTable = ({ data }) => {
  const tableData = useMemo(() => {
    if (!data || !data.values) return [];

    return data.values.map((d) => {
      return {
        dateUnix: d.date,
        date: CalendarUtils.format(d.date, 'DD/MM/YY h:mm A'),
        value: d.value2 ? d.value1 + '/' + d.value2 : d.value1,
      };
    });
  }, [data]);

  const columns = useMemo(() => {
    return [
      {
        Header: 'Date',
        accessor: 'date',
        id: 'date',
        sortable: true,
        width: 150,
      },
      {
        Header: 'Value',
        accessor: 'value',
        id: 'value',
        sortable: true,
        width: 100,
        Cell: ({ value }) => {
          if (typeof value === 'number') {
            return value.toFixed(1);
          }
          return value;
        },
      },
    ];
  }, [data]);

  return (
    <ReactTable
      data={tableData}
      defaultSorted={[
        {
          id: 'dateUnix',
          desc: true,
        },
      ]}
      columns={columns}
      defaultPageSize={25}
      className="-striped -highlight"
      showPagination={tableData.length > 25}
      minRows={0}
    />
  );
};

const AddData = ({ patient, onSave }) => {
  const dispatch = useAppDispatch();

  const metricTypes = useAppSelector((state) => state.healthRecords.metricTypes);

  const [data, setData] = useState([{ date: moment().unix() }]);
  const [loading, setLoading] = useState(false);
  const [allowOverlap, setAllowOverlap] = useState(false);

  useEffect(() => {
    dispatch(api.HealthRecords.getMetricTypes());
  }, [dispatch]);

  const orderedMetrics = useMemo(() => {
    const weight = metricTypes.find((metric) => metric.id === 'weight');
    const height = metricTypes.find((metric) => metric.id === 'height');
    const otherMetricTypes = metricTypes.filter(
      (metric) => metric.id !== 'weight' && metric.id !== 'height',
    );
    return [weight, height, ...PerformanceUtils.sortBy(otherMetricTypes, 'name', false)];
  }, [metricTypes]);

  const autoData = useMemo(() => {
    let result = [];

    let height = data.find((d) => d.id === 'height');
    let width = data.find((d) => d.id === 'weight');
    if (height?.value && width?.value) {
      let bmi = CPPUtils.calculateBMI(parseFloat(height.value), parseFloat(width.value));
      result.push({
        id: 'bmi',
        value: `${bmi}`,
        date: height?.date || width?.date || moment().unix(),
      });
    }

    return result;
  }, [data]);

  async function save() {
    setLoading(true);

    let result = await Promise.all(
      [...data, ...autoData].map(async (d) => {
        let value = d.value.split('/');

        let toSubmit = {
          name: d.id,
          type: d.type,
          values: [{ date: d.date, value: value[0], value2: value[1] }],
        };

        return dispatch(
          api.HealthRecords.addHealthData({
            patientId: patient.id,
            healthData: toSubmit,
            allowOverlap,
          }),
          { errorTitle: 'Add Metric' },
        );
      }),
    );

    setLoading(false);

    if (result[0]) {
      setData([{ date: moment().unix() }]);
      onSave();
    }
  }

  const buttonDisabled = !!data.find((d) => !d.date || !d.value || !d.id);

  function updateData(value, index) {
    setData((prevState) => {
      return prevState.map((item, idx) => {
        if (index === idx) {
          return {
            ...item,
            ...value,
          };
        }
        return item;
      });
    });
  }

  function addRow() {
    setData((prevState) => {
      return [...prevState, { date: moment().unix() }];
    });
  }

  function removeRow(index) {
    setData((prevState) => {
      return prevState.filter((_, idx) => idx !== index);
    });
  }

  return (
    <div className="add-data">
      {[...data, ...autoData].map((d, index) => {
        const types = orderedMetrics.find((metric) => metric.id === d.id)?.types;

        return (
          <div className="metric" key={(d.id || '') + index}>
            <DefaultSelectField
              label="Metric"
              name="name"
              value={d.id}
              disabled={loading}
              style={{ width: '100%' }}
              onChange={(event) => updateData({ id: event.target.value, type: undefined }, index)}
              inputElements={orderedMetrics.map((type) => (
                <MenuItem key={'select-' + type.id} value={type.id}>
                  {`${type.name}${type.units ? ` (${type.units})` : ''}`}
                </MenuItem>
              ))}
            />
            {types ? (
              <DefaultSelectField
                label="Metric"
                name="name"
                value={d.type}
                disabled={loading}
                style={{ width: '100%' }}
                onChange={(event) => updateData({ type: event.target.value }, index)}
                inputElements={types.map((type) => (
                  <MenuItem key={'select-' + type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              />
            ) : null}
            <DefaultTextField
              label="Value"
              name="value"
              value={d.value}
              disabled={loading}
              onChange={(event) => updateData({ value: event.target.value }, index)}
              style={{ width: '100%' }}
            />
            <DefaultDatePicker
              label="Date"
              name="date"
              value={d.date}
              disabled={loading}
              onChange={(event, date) => updateData({ date }, index)}
              style={{ width: '100%' }}
            />
            <div className="buttons">
              {index < data.length && (
                <IconButton
                  onClick={() => removeRow(index)}
                  className="blue"
                  size="small"
                  style={{ width: '30px', height: '30px' }}
                >
                  <Icon src="minus" className="white" showTooltip={true} alt="Remove Row" />
                </IconButton>
              )}
              {index === data.length - 1 && (
                <IconButton
                  onClick={addRow}
                  className="blue"
                  size="small"
                  style={{ width: '30px', height: '30px' }}
                >
                  <Icon src="plus" className="white" showTooltip={true} alt="Add Row" />
                </IconButton>
              )}
            </div>
          </div>
        );
      })}
      <div className="row mt-2 full-width">
        <div className="flex-grow">
          <DefaultSwitch
            name="allowOverlap"
            reversed={true}
            onChange={(event, isInputChecked) => {
              setAllowOverlap(isInputChecked);
            }}
            label="Allow Multiple Values per Day"
            padded={false}
            checked={allowOverlap}
          />
        </div>
        <DefaultButton
          name="Save All"
          onClick={save}
          loading={loading}
          disabled={buttonDisabled}
          className="blue"
        />
      </div>
    </div>
  );
};

const PatientHideMetric = ({ patient, metric }) => {
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);

  const metricHidden = useMemo(() => {
    return (patient.metrics_hidden || []).includes(metric);
  }, [patient.metrics_hidden, metric]);

  async function onChange(event, isInputChecked) {
    let newPatient = PerformanceUtils.clone(patient);
    newPatient.metrics_hidden = newPatient.metrics_hidden || [];
    if (isInputChecked) {
      newPatient.metrics_hidden.push(metric);
    } else {
      newPatient.metrics_hidden = newPatient.metrics_hidden.filter(
        (currentMetric) => currentMetric !== metric,
      );
    }
    setLoading(true);
    await dispatch(api.Patients.updatePatient({ id: newPatient.id, patient: newPatient }));
    setLoading(false);
  }

  return (
    <div className="hide-metric">
      <Switch
        label="Hide from Patient"
        padded={true}
        disabled={loading}
        checked={metricHidden}
        onChange={onChange}
      />
    </div>
  );
};

const MetricBadge = ({ summary }) => {
  let { metric = {}, last_value = {}, type, origin } = summary;
  let { name, short_name, units } = metric;
  let { value1, value2, date } = last_value;

  let title = name || short_name;
  if (type) title += ` (${type})`;

  let value = value1;
  if (value2) value += `/${value2}`;

  let info = value;
  if (units) info += ` ${units}`;
  let info2 = `${CalendarUtils.format(date, 'YYYY-MM-DD')}`;

  return (
    <GenericBadge
      id={metric.short_name}
      className="metric-badge"
      icon={CPPUtils.getMetricOriginSrc(origin)}
      iconAlt={origin}
      title={title}
      avatar={undefined}
      info={
        <span>
          {info} ({info2})
        </span>
      }
    />
  );
};

function convertDate(date, interval) {
  let convertedDate;
  switch (interval) {
    case INTERVALS.day:
      convertedDate = moment.unix(date).format('YYYY-MM-DD');
      break;
    case INTERVALS.month:
      convertedDate = moment.unix(date).format('YYYY-MM');
      break;
    case INTERVALS.year:
      convertedDate = moment.unix(date).format('YYYY');
      break;
    default:
      convertedDate = moment.unix(date).format('YYYY-MM-DD');
  }
  return convertedDate;
}

function convertMetric(summary, metricValue, interval) {
  let { value1, value2, date } = metricValue;
  let name = `${summary.metric.name}`;
  if (summary.metric.units) name += ` (${summary.metric.units})`;

  let convertedDate = convertDate(date, interval);

  let result = [[convertedDate, name, parseFloat(value1)]];
  if (summary.metric.id === 'bp' && !!value2) {
    result = [
      [convertedDate, 'Systolic', parseFloat(value1)],
      [convertedDate, 'Diastolic', parseFloat(value2)],
    ];
  }
  return result;
}

export function convertMetricData(result, selectedSummary, interval) {
  const metricData = result;
  const values = [];
  const sorted = metricData.values;
  const aggregationMethod = selectedSummary.metric.aggregation_method;
  const byInterval = [{}, {}];
  sorted.forEach((val) => {
    let converted = convertMetric(selectedSummary, val, interval);
    converted.forEach((line, index) => {
      let [date, name, value] = line;
      byInterval[index][date] = byInterval[index][date] || {
        date,
        name,
        values: [],
      };
      byInterval[index][date].values.push(value);
    });
  });
  let isAverage = aggregationMethod === 'average';
  byInterval.forEach((line) => {
    Object.values(line).forEach((value) => {
      let calculation = _.sum(value.values);
      if (isAverage) calculation /= value.values.length;
      values.push([value.date, value.name, calculation]);
    });
  });
  return values;
}

Graph.propTypes = {
  patient: PropTypes.object.isRequired,
  defaultMetric: PropTypes.string,
};

export default Graph;
